/**
 * The state of a section read that can keep its last confirmed rows (#1386, used
 * by Org Detail and Trader Detail's Kalshi sections, #1433). Pure, and imports no
 * store: Trader Detail reuses these without loading the account store that
 * `orgKalshiReads.ts` needs for its own account decoding.
 */

/** 'retained': the latest read failed and the panel still shows the last confirmed rows. */
export type OrgReadDisclosure = 'none' | 'retained' | 'unavailable';

export interface OrgReadPanel<T> {
  rows: T[];
  /** A valid read has landed for this org (now or before a retained failure). */
  confirmed: boolean;
  disclosure: OrgReadDisclosure;
}

export const unreadOrgPanel = <T>(): OrgReadPanel<T> => ({ rows: [], confirmed: false, disclosure: 'none' });
/** A failed read with nothing kept: the panel says it did not load. */
export const unavailableOrgPanel = <T>(): OrgReadPanel<T> => ({ rows: [], confirmed: false, disclosure: 'unavailable' });

/**
 * The panel after a read lands. `rows` is null for a failed read. Org Detail's
 * same-org Refresh keeps its rows until the new read lands (#437: retain only
 * where the surface already promises continuity), so a failure keeps the last
 * confirmed rows and says so; with nothing confirmed there is nothing to keep.
 * The org switch resets the panel before its reads, so this never carries
 * another org's rows. Org Detail uses this for orders and fills only; its
 * positions are never retained (see the core landing in index.tsx).
 */
export function nextOrgPanel<T>(prev: OrgReadPanel<T>, rows: T[] | null): OrgReadPanel<T> {
  if (rows) return { rows, confirmed: true, disclosure: 'none' };
  if (prev.confirmed) return { ...prev, disclosure: 'retained' };
  return unavailableOrgPanel();
}

/** A summary tile's count: only a confirmed current read is a number. */
export const orgPanelTileCount = (panel: OrgReadPanel<unknown>, count: number): string =>
  panel.confirmed && panel.disclosure === 'none' ? String(count) : '—';
